export const OPEN_GRAPH_IMAGE_WIDTH = "1200";
export const OPEN_GRAPH_IMAGE_HEIGHT = "630";

export interface SocialMetadata {
  description: string;
  documentDescription: string;
  imageAlt: string;
  imagePath: string;
  title: string;
}

export type SymbolOpenGraphAssetClass = "crypto" | "tradfi";

export const DEFAULT_SOCIAL_METADATA: SocialMetadata = {
  description:
    "Invest in US stocks, gold and crypto with automated portfolios that rebalance on your schedule. Start with $1 and keep assets in your own account.",
  documentDescription:
    "Invest in US stocks, gold and crypto with automated portfolios that rebalance on your schedule. Start with $1 and keep assets in your own account.",
  imageAlt: "Glider investing and portfolio automation preview",
  imagePath: "/v1/og/general.png",
  title: "Glider | Automated Investing in US Stocks, Gold and Crypto",
};

export const EXPLORE_SOCIAL_METADATA: SocialMetadata = {
  description:
    "Discover stocks, crypto, themes, and portfolio starting points on Glider.",
  documentDescription:
    "Discover stocks, crypto, themes, and portfolio starting points on Glider.",
  imageAlt: "Explore markets and portfolio ideas on Glider",
  imagePath: "/v1/og/explore.png",
  title: "Explore | Glider",
};

export const STOCKS_SOCIAL_METADATA: SocialMetadata = {
  description:
    "Explore stock market data, companies, funds, and investing ideas on Glider.",
  documentDescription:
    "Explore stock market data, companies, funds, and investing ideas on Glider.",
  imageAlt: "Explore stocks and stock investing ideas on Glider",
  imagePath: "/v1/og/stocks.png",
  title: "Stocks | Glider",
};

export const CRYPTO_SOCIAL_METADATA: SocialMetadata = {
  description:
    "Browse crypto markets, themes, and portfolio starting points on Glider.",
  documentDescription:
    "Browse crypto markets, themes, and portfolio starting points on Glider.",
  imageAlt: "Explore crypto markets and portfolio ideas on Glider",
  imagePath: "/v1/og/crypto.png",
  title: "Crypto | Glider",
};

export const COMMODITIES_SOCIAL_METADATA: SocialMetadata = {
  description: "Own tokenized gold, silver, and uranium on Glider.",
  documentDescription: "Own tokenized gold, silver, and uranium on Glider.",
  imageAlt: "Explore commodities on Glider",
  imagePath: "/v1/og/general.png",
  title: "Commodities | Glider",
};

export const BONDS_SOCIAL_METADATA: SocialMetadata = {
  description:
    "Sovereign and credit yield from six economies, tokenized on Glider.",
  documentDescription:
    "Sovereign and credit yield from six economies, tokenized on Glider.",
  imageAlt: "Explore bonds and fixed-income opportunities on Glider",
  imagePath: "/v1/og/general.png",
  title: "Bonds | Glider",
};

export const PRE_IPO_SOCIAL_METADATA: SocialMetadata = {
  description:
    "Exposure to the most wanted private companies, before they list.",
  documentDescription:
    "Exposure to the most wanted private companies, before they list.",
  imageAlt: "Explore pre-IPO opportunities on Glider",
  imagePath: "/v1/og/general.png",
  title: "Pre-IPO | Glider",
};

export const BUILD_SOCIAL_METADATA: SocialMetadata = {
  description:
    "Build a personalized portfolio from an index or sector on Glider.",
  documentDescription:
    "Build a personalized portfolio from an index or sector on Glider.",
  imageAlt: "Build your own portfolio on Glider",
  imagePath: "/v1/og/general.png",
  title: "Build | Glider",
};

export const CURRENCIES_SOCIAL_METADATA: SocialMetadata = {
  description: "Hold fully reserved global currencies on Glider.",
  documentDescription: "Hold fully reserved global currencies on Glider.",
  imageAlt: "Explore global currencies on Glider",
  imagePath: "/v1/og/general.png",
  title: "Currencies | Glider",
};

export const PRIVATE_CREDIT_SOCIAL_METADATA: SocialMetadata = {
  description: "Tokenized private-credit funds and lending yield on Glider.",
  documentDescription:
    "Tokenized private-credit funds and lending yield on Glider.",
  imageAlt: "Explore private-credit opportunities on Glider",
  imagePath: "/v1/og/general.png",
  title: "Private credit | Glider",
};

export const STRATEGY_SOCIAL_METADATA: SocialMetadata = {
  description:
    "Explore an investing strategy and its target allocations on Glider.",
  documentDescription:
    "View this investing strategy and its target allocations on Glider.",
  imageAlt: "Glider investing strategy preview",
  imagePath: "/v1/og/general.png",
  title: "Strategy | Glider",
};

export const PORTFOLIO_SOCIAL_METADATA: SocialMetadata = {
  description:
    "Explore a public investing portfolio and its target allocations on Glider.",
  documentDescription:
    "View this public investing portfolio and its target allocations on Glider.",
  imageAlt: "Glider public portfolio preview",
  imagePath: "/v1/og/general.png",
  title: "Portfolio | Glider",
};

export function createPortfolioSocialMetadata(input: {
  readonly description: string | null;
  readonly name: string;
  readonly portfolioId: string;
}): SocialMetadata {
  const description =
    input.description ??
    `Explore ${input.name} and its target allocations on Glider.`;
  return {
    description,
    documentDescription: description,
    imageAlt: `${input.name} public portfolio preview on Glider`,
    imagePath: `/v1/og/portfolio/${encodeURIComponent(input.portfolioId)}.png`,
    title: `${input.name} | Glider`,
  };
}

export function createStrategySocialMetadata(input: {
  readonly description: string | null;
  readonly name: string;
  readonly strategyId: string;
}): SocialMetadata {
  const description =
    input.description ??
    `Explore ${input.name} and its target allocations on Glider.`;
  return {
    description,
    documentDescription: description,
    imageAlt: `${input.name} strategy preview on Glider`,
    imagePath: `/v1/og/strategy/${encodeURIComponent(input.strategyId)}.png`,
    title: `${input.name} | Glider`,
  };
}

export function createSymbolSocialMetadata(
  symbol: string,
  options?: {
    assetClass?: SymbolOpenGraphAssetClass;
    title?: string;
  },
): SocialMetadata {
  const normalizedSymbol = symbol.trim().toUpperCase();
  const imagePath = `/v1/og/symbol/${encodeURIComponent(normalizedSymbol)}.png`;
  return {
    description: `Explore ${normalizedSymbol} market data and investing information on Glider.`,
    documentDescription: `Explore ${normalizedSymbol} market data, price context, and investing information on Glider.`,
    imageAlt: `${normalizedSymbol} market snapshot on Glider`,
    imagePath: options?.assetClass
      ? `${imagePath}?assetClass=${options.assetClass}`
      : imagePath,
    title: options?.title ?? `${normalizedSymbol} | Glider`,
  };
}

const RECEIPT_SHARE_SLUG_PATTERN = /^[a-zA-Z0-9_-]{16,64}$/;

/**
 * Public receipt links (/r/:slug) unfurl with a per-receipt card rendered by
 * the platform-api OG route (interim home until the OpenGraph-images worker
 * ships). The slug is safe to embed: it's the share secret the visitor
 * already holds by having the link.
 */
export function getReceiptShareSocialMetadata(
  pathname: string,
): SocialMetadata | null {
  const normalizedPathname = normalizePathname(pathname);
  if (!normalizedPathname.startsWith("/r/")) {
    return null;
  }
  const slug = normalizedPathname.slice("/r/".length);
  if (!RECEIPT_SHARE_SLUG_PATTERN.test(slug)) {
    return null;
  }
  return {
    description:
      "A Glider activity receipt — deposits, orders, and portfolio updates with on-chain proof.",
    documentDescription:
      "View this Glider activity receipt: status, amounts, and on-chain transactions.",
    imageAlt: "Glider activity receipt preview",
    imagePath: `/v1/og/receipt/${slug}.png`,
    title: "Receipt | Glider",
  };
}

const DISCOVERY_ROUTE_SOCIAL_METADATA_BY_PATHNAME = {
  "/bonds": BONDS_SOCIAL_METADATA,
  "/commodities": COMMODITIES_SOCIAL_METADATA,
  "/crypto": CRYPTO_SOCIAL_METADATA,
  "/currencies": CURRENCIES_SOCIAL_METADATA,
  "/build": BUILD_SOCIAL_METADATA,
  "/explore": EXPLORE_SOCIAL_METADATA,
  "/pre-ipo": PRE_IPO_SOCIAL_METADATA,
  "/private-credit": PRIVATE_CREDIT_SOCIAL_METADATA,
  "/stocks": STOCKS_SOCIAL_METADATA,
} as const satisfies Record<string, SocialMetadata>;

type DiscoveryRoutePathname =
  keyof typeof DISCOVERY_ROUTE_SOCIAL_METADATA_BY_PATHNAME;

function normalizePathname(pathname: string): string {
  if (!pathname || pathname === "/") {
    return "/";
  }

  return pathname.endsWith("/") ? pathname.slice(0, -1) : pathname;
}

export function getDiscoveryRouteSocialMetadata(
  pathname: string,
): SocialMetadata | null {
  const normalizedPathname = normalizePathname(pathname);
  if (
    !Object.hasOwn(
      DISCOVERY_ROUTE_SOCIAL_METADATA_BY_PATHNAME,
      normalizedPathname,
    )
  ) {
    return null;
  }

  // Object.hasOwn above proves the normalized path is a registry key.
  const routePathname = normalizedPathname as DiscoveryRoutePathname;
  return DISCOVERY_ROUTE_SOCIAL_METADATA_BY_PATHNAME[routePathname];
}
